import { useRef, useEffect } from "preact/hooks";
import {
  archiveSources,
  archiveSourcesLoaded,
  disabledSources,
  liveEnabled,
  isSourceEnabled,
  toggleSource,
  enableAllSources,
  disableAllSources,
  LIVE_SOURCE_KEY,
  meta,
} from "../state/store";

/** The Sources facet: where each record comes from, live pipeline and archives alike,
 *  as one checklist. This is the ONLY place the product distinguishes "upcoming" from
 *  "historical" data provenance — everything else is just the time axis.
 *
 *  A native <details> so it's keyboard/screen-reader correct without a popover
 *  reimplementation; the only added behavior is close-on-outside-click. */
export function SourcesSelect() {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const sources = archiveSources.value;
  const loaded = archiveSourcesLoaded.value;
  const disabled = disabledSources.value;
  const live = liveEnabled.value;
  const liveCount = meta.value?.concert_count;

  const total = 1 + sources.length;
  const enabledCount = (live ? 1 : 0) + sources.filter(isSourceEnabled).length;

  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      const el = detailsRef.current;
      if (el?.open && !el.contains(e.target as Node)) el.open = false;
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  return (
    <details class="sources-select" ref={detailsRef}>
      <summary class="sources-summary">
        Sources
        <span class="sources-summary-count">
          {enabledCount === total ? "all" : `${enabledCount}/${total}`}
        </span>
      </summary>
      <div class="sources-menu">
        <label class="source-row">
          <input
            type="checkbox"
            checked={live}
            onChange={() => toggleSource(LIVE_SOURCE_KEY)}
          />
          <span class="source-row-main">
            <span class="source-row-name">Live listings</span>
            <span class="source-row-detail">upcoming &amp; recent seasons, from venue websites</span>
          </span>
          {liveCount != null && (
            <span class="source-row-count">{liveCount.toLocaleString()}</span>
          )}
        </label>

        {sources.map((s) => (
          <label class="source-row" key={s.key}>
            <input
              type="checkbox"
              checked={isSourceEnabled(s)}
              onChange={() => toggleSource(s.key)}
            />
            <span class="source-row-main">
              <span class="source-row-name">
                {s.title}
                {s.status !== "confirmed" && (
                  <span
                    class="confidence-badge"
                    title="Lower-confidence source — crowdsourced rather than an institutional archive."
                  >
                    {s.status}
                  </span>
                )}
              </span>
              <span class="source-row-detail">
                {s.year_min === s.year_max ? s.year_min : `${s.year_min}–${s.year_max}`}
                {s.license ? ` · ${s.license}` : ""}
              </span>
            </span>
            <span class="source-row-count">{s.count.toLocaleString()}</span>
          </label>
        ))}

        {!loaded && sources.length === 0 && (
          <div class="sources-note">Loading archive sources…</div>
        )}

        {/* Bulk actions: "Clear all" empties the checklist in one click (the usual next
            step is ticking exactly one source back on), "Enable all" undoes any pruning. */}
        <div class="sources-actions">
          {enabledCount > 0 && (
            <button class="sources-action-btn" type="button" onClick={disableAllSources}>
              Clear all
            </button>
          )}
          {disabled.size > 0 && (
            <button class="sources-action-btn" type="button" onClick={enableAllSources}>
              Enable all
            </button>
          )}
        </div>

        <div class="sources-note">
          Archive sources hold recent seasons back when the live pipeline already covers
          them, to avoid double-counting.
        </div>
      </div>
    </details>
  );
}
